import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  ShoppingBagIcon,
  ClockIcon,
  CreditCardIcon,
  TruckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, Avatar, Badge, Spinner, Button, Modal } from '../components/ui';
import { usePatientStore } from '../stores';
import { patientService } from '../services/patient.service';
import { formatDate, formatPhone, formatCurrency } from '../utils/helpers';
import { getStatusDisplayName, getStatusBadgeVariant } from '../utils/orderStatus';
import type { PatientOrder, EmailLog } from '../types';

export const PatientDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedPatient, getPatientById, isLoading } = usePatientStore();
  const [orders, setOrders] = useState<PatientOrder[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);

  useEffect(() => {
    if (id) {
      getPatientById(id);
      fetchPatientOrders(id);
      fetchPatientEmailLogs(id);
    }
  }, [id, getPatientById]);

  const fetchPatientOrders = async (patientId: string) => {
    setIsLoadingOrders(true);
    try {
      const patientOrders = await patientService.getPatientOrders(patientId);
      setOrders(patientOrders);
    } catch (error) {
      console.error('Failed to fetch patient orders:', error);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const fetchPatientEmailLogs = async (patientId: string) => {
    setIsLoadingEmails(true);
    try {
      const logs = await patientService.getPatientEmailLogs(patientId);
      setEmailLogs(logs);
    } catch (error) {
      console.error('Failed to fetch patient email logs:', error);
    } finally {
      setIsLoadingEmails(false);
    }
  };

  // Using shared getStatusBadgeVariant from utils/orderStatus.ts

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!selectedPatient) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500 dark:text-secondary-400">Patient not found</p>
        <Button variant="secondary" onClick={() => navigate('/patients')} className="mt-4">
          Back to Patients
        </Button>
      </div>
    );
  }

  const patient = selectedPatient;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/patients')}
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-secondary-500 dark:text-secondary-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Patient Details</h1>
          <p className="text-secondary-500 dark:text-secondary-400">View patient information</p>
        </div>
      </div>

      {/* Profile Card */}
      <Card>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <Avatar
            firstName={patient.firstName}
            lastName={patient.lastName}
            size="xl"
          />
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                {patient.firstName} {patient.lastName}
              </h2>
              <Badge variant="success" dot>
                Active
              </Badge>
            </div>
            <p className="text-secondary-500 dark:text-secondary-400 mt-1">Patient ID: {patient.id}</p>
          </div>
        </div>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader title="Contact Information" />
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30">
                <EnvelopeIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Email</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{patient.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30">
                <PhoneIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Phone</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{formatPhone(patient.phone)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30">
                <MapPinIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Address</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">
                  {patient.address}<br />
                  {patient.city}, {patient.state} {patient.zipCode}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader title="Personal Information" />
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-accent-50 dark:bg-accent-900/30">
                <CalendarIcon className="h-5 w-5 text-accent-600 dark:text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Date of Birth</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{formatDate(patient.dateOfBirth)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-accent-50 dark:bg-accent-900/30">
                <UserIcon className="h-5 w-5 text-accent-600 dark:text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Gender</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100 capitalize">{patient.gender}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-accent-50 dark:bg-accent-900/30">
                <CalendarIcon className="h-5 w-5 text-accent-600 dark:text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Last Visit</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">
                  {patient.lastVisit ? formatDate(patient.lastVisit) : 'No visits yet'}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Timeline/Activity Card */}
      <Card>
        <CardHeader title="Account Information" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">Member Since</p>
            <p className="font-medium text-secondary-900 dark:text-secondary-100">{formatDate(patient.createdAt)}</p>
          </div>
          <div>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">Last Updated</p>
            <p className="font-medium text-secondary-900 dark:text-secondary-100">{formatDate(patient.updatedAt)}</p>
          </div>
        </div>
      </Card>

      {/* Order History */}
      <Card>
        <CardHeader 
          title="Order History" 
          subtitle={`${orders.length} order${orders.length !== 1 ? 's' : ''}`}
        />
        
        {isLoadingOrders ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingBagIcon className="h-12 w-12 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
            <p className="text-secondary-500 dark:text-secondary-400">No orders found for this patient</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 hover:border-secondary-300 dark:hover:border-secondary-600 transition-colors"
              >
                {/* Order Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-secondary-900 dark:text-secondary-100">
                        Order #{order.orderCode || order.id.slice(0, 8)}
                      </span>
                      <Badge variant={getStatusBadgeVariant(order.status)} dot>
                        {getStatusDisplayName(order.status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                      Placed on {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-secondary-900 dark:text-secondary-100">
                      {formatCurrency(order.totalAmount)}
                    </p>
                  </div>
                </div>

                {/* Order Details */}
                {order.offering && (
                  <div className="flex items-center gap-3 py-3 border-t border-secondary-100 dark:border-secondary-700">
                    <div className="w-12 h-12 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center overflow-hidden shrink-0">
                      {order.offering.imageUrl ? (
                        <img
                          src={order.offering.imageUrl}
                          alt={order.offering.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ShoppingBagIcon className="h-6 w-6 text-secondary-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-secondary-900 dark:text-secondary-100 truncate">
                        {order.offering.name}
                      </p>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate">
                        {order.offering.dosage || order.offering.description}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-secondary-900 dark:text-secondary-100">Qty: {order.offering.quantity}</p>
                      <p className="text-secondary-500 dark:text-secondary-400">
                        {(order.offering?.monthSupply ?? 0) > 0
                          ? `${order.offering?.monthSupply} month supply`
                          : 'One-time purchase'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Order Meta */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-secondary-100 dark:border-secondary-700 text-sm">
                  {/* Payment Method */}
                  {order.paymentMethod && (
                    <div className="flex items-center gap-2">
                      <CreditCardIcon className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
                      <span className="text-secondary-600 dark:text-secondary-400">
                        {order.paymentMethod.cardType} •••• {order.paymentMethod.lastFour}
                      </span>
                    </div>
                  )}

                  {/* Shipping */}
                  {order.shippingAddress && (
                    <div className="flex items-center gap-2">
                      <TruckIcon className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
                      <span className="text-secondary-600 dark:text-secondary-400 truncate">
                        {order.shippingAddress.city}, {order.shippingAddress.state}
                      </span>
                    </div>
                  )}

                  {/* Refills - only show for subscription orders */}
                  {(order.offering?.monthSupply ?? 0) > 0 && order.refillsRemaining !== undefined && order.refillsRemaining >= 0 && (
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
                      <span className="text-secondary-600 dark:text-secondary-400">
                        {order.refillsRemaining} refill{order.refillsRemaining !== 1 ? 's' : ''} left
                      </span>
                    </div>
                  )}

                  {/* One-time purchase indicator */}
                  {(order.offering?.monthSupply ?? 0) === 0 && (
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-success-500 dark:text-success-400" />
                      <span className="text-secondary-600 dark:text-secondary-400">
                        One-time purchase
                      </span>
                    </div>
                  )}

                  {/* Next Refill - only show for subscription orders */}
                  {(order.offering?.monthSupply ?? 0) > 0 && order.nextRefill && (
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
                      <span className="text-secondary-600 dark:text-secondary-400">
                        Next: {formatDate(order.nextRefill)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Transactions */}
                {order.transactions && order.transactions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-secondary-100 dark:border-secondary-700">
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase mb-2">
                      Transactions
                    </p>
                    <div className="space-y-1">
                      {order.transactions.map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={tx.status === 'settled' || tx.status === 'captured' ? 'success' : 'secondary'}
                              className="text-xs"
                            >
                              {tx.status}
                            </Badge>
                            <span className="text-secondary-500 dark:text-secondary-400">
                              {formatDate(tx.createdAt)}
                            </span>
                          </div>
                          <span className="font-medium text-secondary-900 dark:text-secondary-100">
                            {formatCurrency(tx.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Email History */}
      <Card>
        <CardHeader
          title="Email History"
          subtitle={`${emailLogs.length} email${emailLogs.length !== 1 ? 's' : ''} sent`}
        />

        {isLoadingEmails ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        ) : emailLogs.length === 0 ? (
          <div className="text-center py-8">
            <EnvelopeIcon className="h-12 w-12 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
            <p className="text-secondary-500 dark:text-secondary-400">No emails sent to this patient</p>
          </div>
        ) : (
          <div className="space-y-3">
            {emailLogs.map((email) => (
              <div
                key={email.id}
                className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 hover:border-secondary-300 dark:hover:border-secondary-600 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge
                        variant={email.status === 'SUCCESS' ? 'success' : 'danger'}
                        dot
                      >
                        {email.status}
                      </Badge>
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">
                        {formatDate(email.sentAt)}
                      </span>
                    </div>
                    <p className="font-medium text-secondary-900 dark:text-secondary-100 truncate">
                      {email.subject}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                      <span className="flex items-center gap-1">
                        <EnvelopeIcon className="h-4 w-4" />
                        {email.recipientEmail}
                      </span>
                      <span className="text-secondary-400 dark:text-secondary-600">•</span>
                      <span className="capitalize">{email.templateName.replace(/_/g, ' ').toLowerCase()}</span>
                    </div>
                    {email.error && (
                      <p className="mt-2 text-sm text-danger-600 dark:text-danger-400">
                        Error: {email.error}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSelectedEmail(email)}
                    className="ml-4 shrink-0"
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Email Body Modal */}
      <Modal
        isOpen={selectedEmail !== null}
        onClose={() => setSelectedEmail(null)}
        title={selectedEmail?.subject || 'Email Details'}
        size="lg"
      >
        {selectedEmail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 pb-4 border-b border-secondary-200 dark:border-secondary-700">
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Status</p>
                <Badge variant={selectedEmail.status === 'SUCCESS' ? 'success' : 'danger'} dot className="mt-1">
                  {selectedEmail.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Sent Date</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100 mt-1">
                  {formatDate(selectedEmail.sentAt)}
                </p>
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Recipient</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100 mt-1">
                  {selectedEmail.recipientEmail}
                </p>
              </div>
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">Template</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100 mt-1 capitalize">
                  {selectedEmail.templateName.replace(/_/g, ' ').toLowerCase()}
                </p>
              </div>
            </div>

            {selectedEmail.error && (
              <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800">
                <p className="text-sm font-medium text-danger-900 dark:text-danger-200">Error</p>
                <p className="text-sm text-danger-700 dark:text-danger-300 mt-1">{selectedEmail.error}</p>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Email Body</p>
              <div
                className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-lg bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmail.body) }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientDetail;


